import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  Heart, 
  Phone, 
  Mail, 
  Globe, 
  MapPin, 
  Clock,
  ArrowLeft,
  ExternalLink,
  Users,
  Calendar,
  Star,
  Shield,
  Utensils,
  Camera,
  Music,
  MessageSquare,
  Send
} from "lucide-react";
import { Link } from "wouter";
import type { WelcomeConfig } from "@shared/schema";
import { parseFounderLetter } from "@/lib/founderLetter";
import { apiRequest } from "@/lib/queryClient";

const emailCeoSchema = z.object({
  subject: z.string().min(1, "Subject is required"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  priority: z.enum(["low", "medium", "high"]).default("medium")
});

type EmailCeoFormData = z.infer<typeof emailCeoSchema>;

export default function WelcomePage() {
  const { data: config, isLoading } = useQuery<WelcomeConfig>({
    queryKey: ["/api/welcome/config"],
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<EmailCeoFormData>({
    resolver: zodResolver(emailCeoSchema),
    defaultValues: {
      subject: "",
      message: "",
      priority: "medium"
    }
  });

  const emailCeoMutation = useMutation({
    mutationFn: async (data: EmailCeoFormData) => {
      const emailContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
          <div style="background-color: #1a1a1a; color: white; padding: 20px; border-radius: 8px;">
            <h2 style="color: #B8860B; margin-top: 0;">LA Mirage - Client Escalation</h2>
            <div style="background-color: ${data.priority === 'high' ? '#ef4444' : data.priority === 'medium' ? '#f59e0b' : '#10b981'}; color: white; padding: 8px 16px; border-radius: 4px; margin-bottom: 20px; display: inline-block;">
              ${data.priority.toUpperCase()} PRIORITY
            </div>
            
            <div style="background-color: #2a2a2a; padding: 15px; border-radius: 4px; margin: 15px 0;">
              <h3 style="color: #B8860B; margin-top: 0;">Subject:</h3>
              <p style="margin-bottom: 0;">${data.subject}</p>
            </div>
            
            <div style="background-color: #2a2a2a; padding: 15px; border-radius: 4px; margin: 15px 0;">
              <h3 style="color: #B8860B; margin-top: 0;">Message:</h3>
              <p style="white-space: pre-line; margin-bottom: 0;">${data.message}</p>
            </div>
            
            <hr style="border: 1px solid #444; margin: 20px 0;">
            <p style="color: #888; font-size: 12px; margin-bottom: 0;">
              Sent from LA Mirage Welcome Page<br>
              This is an escalation/notification from a client<br>
              Timestamp: ${new Date().toLocaleString()}
            </p>
          </div>
        </div>
      `;

      console.log("Sending email with data:", data);
      
      try {
        const response = await fetch("/api/client/email-ceo", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: data.subject,
            message: data.message,
            priority: data.priority
          })
        });

        console.log("Email API response status:", response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Email API error:", errorText);
          throw new Error(`Failed to send email: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log("Email sent successfully:", result);
        return result;
      } catch (error) {
        console.error("Email sending error:", error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Email Sent Successfully",
        description: "Your message has been sent to the CEO. You will receive a response within 24 hours.",
      });
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Send Email",
        description: error.message || "Please try again or contact support directly.",
        variant: "destructive",
      });
    }
  });

  const onSubmitEmail = (data: EmailCeoFormData) => {
    emailCeoMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-yellow"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Welcome message not available</h1>
          <Link href="/dashboard">
            <Button className="bg-dark-yellow hover:bg-dark-yellow/90 text-black">
              Return to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Letter photo: tall portrait. The admin-configured founderImageUrl is the round
  // avatar used on the dashboard card, so the letter uses its own portrait crop.
  const letterPhoto = '/founder/lijo-varghese-letter.jpg';
  const letter = parseFounderLetter(config.fullMessage);
  const firstName = (config.founderName || '').split(' ')[0] || config.founderName;

  const emailDialog = (
                  <Dialog>
                    <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-11 rounded-full border-[#C9A24A]/50 bg-transparent px-5 text-[#E3C77E] hover:bg-[#C9A24A]/10 hover:text-[#F1DDA8]"
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      Email CEO
                    </Button>
                  </DialogTrigger>
                    <DialogContent className="sm:max-w-[525px] bg-black/95 border-dark-yellow/20">
                      <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                          <MessageSquare className="w-5 h-5 text-red-400" />
                          Email CEO - Escalation & Issues
                        </DialogTitle>
                        <p className="text-gray-400 text-sm">
                          Send a direct message to lijo@la-mirage.in for urgent matters or issues that need immediate attention.
                        </p>
                      </DialogHeader>
                      
                      <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmitEmail)} className="space-y-4">
                          <FormField
                            control={form.control}
                            name="priority"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-white">Priority Level</FormLabel>
                                <FormControl>
                                  <select 
                                    {...field}
                                    className="w-full p-2 bg-black/60 border border-dark-yellow/30 rounded-md text-white focus:border-dark-yellow focus:outline-none"
                                  >
                                    <option value="low">Low Priority</option>
                                    <option value="medium">Medium Priority</option>
                                    <option value="high">High Priority</option>
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="subject"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-white">Subject</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field}
                                    placeholder="Brief description of the issue or request"
                                    className="bg-black/60 border-dark-yellow/30 text-white placeholder:text-gray-500 focus:border-dark-yellow"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="message"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-white">Message</FormLabel>
                                <FormControl>
                                  <Textarea 
                                    {...field}
                                    placeholder="Describe the issue, escalation, or feedback in detail..."
                                    rows={6}
                                    className="bg-black/60 border-dark-yellow/30 text-white placeholder:text-gray-500 focus:border-dark-yellow resize-none"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="flex gap-3 pt-4">
                            <DialogTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                              >
                                Cancel
                              </Button>
                            </DialogTrigger>
                            <Button
                              type="submit"
                              disabled={emailCeoMutation.isPending}
                              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            >
                              {emailCeoMutation.isPending ? (
                                <div className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Sending...
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Send className="w-4 h-4" />
                                  Send to CEO
                                </div>
                              )}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>
  );

  return (
    <div className="min-h-screen bg-[#0d0b09] text-[#EDE5D6]">
      {/* Top bar */}
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 pt-6 sm:px-6">
        <Link href="/">
          <Button variant="ghost" size="sm" className="rounded-full text-[#BFB29A] hover:bg-white/5 hover:text-white">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
        </Link>
        <span className="text-xs tracking-[0.18em] text-[#8F8573]">A LETTER FROM THE FOUNDER</span>
      </div>

      {/* Letter */}
      <article className="mx-auto grid max-w-6xl gap-10 px-4 pb-16 pt-8 sm:px-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16 lg:pt-12">
        {/* Portrait */}
        <aside className="lg:sticky lg:top-8 lg:self-start">
          <figure className="relative mx-auto max-w-sm lg:max-w-none">
            <div className="absolute -inset-3 rounded-[28px] border border-[#C9A24A]/25" aria-hidden />
            <img
              src={letterPhoto}
              alt={`${config.founderName}, ${config.founderTitle}`}
              width={720}
              height={900}
              className="relative aspect-[4/5] w-full rounded-[22px] object-cover shadow-[0_30px_80px_-30px_rgba(201,162,74,0.35)]"
            />
            <figcaption className="relative mt-6 text-center lg:text-left">
              <p className="font-['Playfair_Display'] text-2xl text-white">{config.founderName}</p>
              <p className="mt-1 text-sm text-[#C9A24A]">{config.founderTitle}</p>
              <p className="mt-0.5 text-sm text-[#8F8573]">LA Mirage Convention Center, Koratty</p>
              <div className="mt-5 flex justify-center lg:justify-start">{emailDialog}</div>
            </figcaption>
          </figure>
        </aside>

        {/* Body */}
        <div className="min-w-0">
          {letter.greeting && <p className="text-lg text-[#BFB29A]">{letter.greeting}</p>}
          {letter.headline && (
            <h1 className="mt-3 font-['Playfair_Display'] text-[34px] leading-[1.15] text-white sm:text-5xl">
              {letter.headline}
            </h1>
          )}
          <div className="mt-6 h-px w-24 bg-gradient-to-r from-[#C9A24A] to-transparent" aria-hidden />

          <div className="mt-8 space-y-6 text-[17px] leading-[1.8] text-[#D9D0BF]">
            {letter.blocks.map((b, i) =>
              b.kind === 'quote' ? (
                <blockquote key={i} className="relative my-10 border-l-2 border-[#C9A24A] pl-6">
                  <span className="absolute -left-1 -top-8 font-['Playfair_Display'] text-7xl leading-none text-[#C9A24A]/30" aria-hidden>“</span>
                  {b.lines.map((l, k) => (
                    <p key={k} className="font-['Playfair_Display'] text-2xl italic leading-snug text-[#F1E6CC] sm:text-[28px]">{l}</p>
                  ))}
                </blockquote>
              ) : b.kind === 'emphasis' ? (
                <div key={i} className="py-1">
                  {b.lines.map((l, k) => (
                    <p key={k} className="font-['Playfair_Display'] text-xl leading-snug text-white sm:text-[22px]">{l}</p>
                  ))}
                </div>
              ) : (
                <p key={i}>{b.text}</p>
              )
            )}
          </div>

          {(letter.closing || letter.signature) && (
            <div className="mt-12">
              {letter.closing && <p className="text-[#BFB29A]">{letter.closing}</p>}
              {letter.signature && (
                <p className="mt-2 font-['Playfair_Display'] text-3xl italic text-[#E3C77E]">{letter.signature}</p>
              )}
            </div>
          )}
          {letter.motto && (
            <p className="mt-8 font-['Playfair_Display'] text-lg italic text-[#C9A24A]">“{letter.motto}”</p>
          )}

          {letter.contacts.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2 border-t border-white/10 pt-6">
              {letter.contacts.map((c, i) =>
                c.href ? (
                  <a key={i} href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
                     className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/10 px-4 text-sm text-[#D9D0BF] hover:border-[#C9A24A]/50 hover:text-white">
                    {c.label && <span className="text-[#8F8573]">{c.label}</span>}
                    <span>{c.value}</span>
                  </a>
                ) : (
                  <span key={i} className="inline-flex min-h-[44px] items-center px-1 text-sm text-[#8F8573]">{c.value}</span>
                )
              )}
            </div>
          )}
        </div>
      </article>

      {/* Policies & updates (the rest of the owner's text) */}
      {letter.policies.length > 0 && (
        <section className="border-t border-white/10 bg-[#12100d]">
          <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
            <h2 className="font-['Playfair_Display'] text-2xl text-white">Venue policies and updates</h2>
            <p className="mt-1 text-sm text-[#8F8573]">Please share these with your caterer and planners.</p>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {letter.policies.map((g, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <p className="font-medium leading-snug text-white">{g.title}</p>
                  {g.body.map((b, k) => (
                    <p key={k} className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#BFB29A]">{b}</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contacts */}
      <section className="mx-auto grid max-w-6xl gap-4 px-4 py-12 sm:grid-cols-2 sm:px-6">
        <div className="rounded-2xl border border-white/10 p-5">
          <p className="text-sm text-[#C9A24A]">Founder & Managing Director</p>
          <div className="mt-3 space-y-2 text-sm">
            {config.contactPhone && <a href={`tel:${String(config.contactPhone).replace(/[^\d+]/g, '')}`} className="flex min-h-[44px] items-center gap-3 text-white"><Phone className="h-4 w-4 text-[#C9A24A]" />{config.contactPhone}</a>}
            {config.contactEmail && <a href={`mailto:${config.contactEmail}`} className="flex min-h-[44px] items-center gap-3 text-white"><Mail className="h-4 w-4 text-[#C9A24A]" />{config.contactEmail}</a>}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 p-5">
          <p className="text-sm text-[#C9A24A]">Event Manager</p>
          <p className="mt-3 text-white">{config.eventManagerName}</p>
          {config.eventManagerPhone && <a href={`tel:${String(config.eventManagerPhone).replace(/[^\d+]/g, '')}`} className="flex min-h-[44px] items-center gap-3 text-sm text-white"><Phone className="h-4 w-4 text-[#C9A24A]" />{config.eventManagerPhone}</a>}
        </div>
      </section>
    </div>
  );
}
